import { FC } from "react";

import { Locales, QuestionType } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { NewsletterFormQuestion, NewsletterFormSignupData } from "../types";
import { newsletterSelectIconSx, newsletterSelectPlaceholderSx } from "../helpers/fieldStyles";
import { getQuestionChoices, getQuestionLabel } from "../helpers/questionAnswers";

import { NewsletterQuestionChoicesField } from "./NewsletterQuestionChoicesField";

import { isEmailAndAllowPlus, isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox";
import { DashboardDatePicker } from "@/dashboard-design-system/DashboardDatePicker";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

type NewsletterQuestionFieldProps = {
  form: UseFormReturn<NewsletterFormSignupData>;
  question: NewsletterFormQuestion;
  index: number;
  locale: Locales;
};

export const NewsletterQuestionField: FC<NewsletterQuestionFieldProps> = ({ form, question, index, locale }) => {
  const { t } = useTranslate();

  const label = getQuestionLabel(question, locale);
  const { required } = question;
  const requiredRules = required ? [isRequired(t("common", "answerRequired"))] : [];

  const answerName = `questions.${index}.answer` as const;
  const questionErrors = form.formState.errors.questions?.[index];
  const answerError = questionErrors?.answer?.message;

  switch (question.type) {
    case QuestionType.MultipleCheckboxes:
      return (
        <NewsletterQuestionChoicesField
          form={form}
          index={index}
          label={label}
          required={required}
          choices={getQuestionChoices(question, locale)}
        />
      );

    case QuestionType.Multiple: {
      const choices = getQuestionChoices(question, locale);
      const choiceError = questionErrors?.choiceId?.message;

      return (
        <DashboardSelect
          control={form.control}
          name={`questions.${index}.choiceId`}
          rules={requiredRules}
          required={required}
          renderValue={(value) => {
            const selected = choices.find((choice) => choice.id === value);

            if (!selected) {
              return (
                <DashboardTypography variant="body2" sx={newsletterSelectPlaceholderSx}>
                  {label}
                </DashboardTypography>
              );
            }

            return selected.body;
          }}
          error={Boolean(choiceError)}
          helperText={choiceError}
          sx={newsletterSelectIconSx}
          data-test="form-question-select"
        >
          {choices.map((choice) => (
            <DashboardMenuItem key={choice.id} value={choice.id} data-test="form-question-select-option">
              {choice.body}
            </DashboardMenuItem>
          ))}
        </DashboardSelect>
      );
    }

    case QuestionType.Checkbox:
      return (
        <DashboardCheckbox
          control={form.control}
          name={`questions.${index}.isChecked`}
          label={label}
          rules={requiredRules}
          data-test="form-question-checkbox"
        />
      );

    case QuestionType.Date:
      return (
        <DashboardDatePicker
          control={form.control}
          name={answerName}
          label={label}
          rules={requiredRules}
          error={Boolean(answerError)}
          errorMessage={answerError}
          data-test="form-question-date"
        />
      );

    // Every remaining type is a text input; only keyboard type, validators and test id vary. Phone is a
    // tel input because the international widget needs a remount lifecycle this answer array cannot give.
    default: {
      const isEmail = question.type === QuestionType.Email;
      const isPhoneNumber =
        question.type === QuestionType.PhoneNumber || question.type === QuestionType.NotificationPhoneNumber;

      return (
        <DashboardTextField
          control={form.control}
          name={answerName}
          label={label}
          type={isEmail ? "email" : isPhoneNumber ? "tel" : undefined}
          required={required}
          rules={
            isEmail
              ? [
                  ...requiredRules,
                  isEmailAndAllowPlus(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
                ]
              : requiredRules
          }
          error={Boolean(answerError)}
          helperText={answerError}
          data-test={
            isEmail ? "form-question-email" : isPhoneNumber ? "form-question-phone-number" : "form-question-text"
          }
        />
      );
    }
  }
};
